import { useContext, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, ExternalLink } from 'lucide-react';
import { cn } from 'lib/utils';
import AppContext from 'contexts/AppContext';
import AuditDrawerFrame from 'components/admin/audit/AuditDrawerFrame';
import { actorLabel, dayKey, formatTime, numericDate, realignedSector, realignResult, textOr } from 'utils/auditLabels';
import { getJiraIssueUrl } from 'utils/jiraUrls';
import type { AdminAuditEvent } from 'api/adminAuditApi';
import { deskNameOf } from 'api/sectorAlignmentApi';

const SECTOR_CARD = 'rounded-[10px] border bg-background px-3.5 py-3';
const CARD_LABEL = 'text-[11.5px] font-semibold uppercase text-muted-foreground';

/** 8c: a realigned ticket's drawer — Jira link, before/after sector, store, result and failure reason. */
const RealignedTicketDetail = ({ event, open, onOpenChange }: { event: AdminAuditEvent; open: boolean; onOpenChange: (open: boolean) => void }) => {
  const { t } = useTranslation();
  const { jiraBrowseBase } = useContext(AppContext);
  const { from, to } = realignedSector(event);
  const result = realignResult(t, event);
  const store = textOr(event.context?.store, '');
  const desk = deskNameOf(event.context?.sd);
  const error = textOr(event.context?.error, '');
  const when = t('{{date}} at {{time}}', { date: numericDate(dayKey(event.occurredAt)), time: formatTime(event.occurredAt) });
  const rows: [string, ReactNode][] = [
    [t('Ticket'), <span className="font-mono">{event.entityId}</span>],
    [t('Store'), [store, desk].filter(Boolean).join(' · ') || '—'],
    [
      t('Result'),
      <span className="inline-flex items-center gap-2">
        <span className={cn('h-[7px] w-[7px] rounded-full', result.failed ? 'bg-rose-500' : 'bg-emerald-500')} aria-hidden="true" />
        {result.label}
      </span>,
    ],
  ];
  if (error) rows.push([t('Reason'), <span className="text-rose-600 dark:text-rose-400">{error}</span>]);

  return (
    <AuditDrawerFrame
      open={open}
      onOpenChange={onOpenChange}
      title={[event.entityId, store].filter(Boolean).join(' · ')}
      description={[t('Sector realignment'), desk, when, actorLabel(t, event.actor, event.context)].filter(Boolean).join(' · ')}
    >
      <div className="flex flex-col gap-[18px]">
        <a
          href={getJiraIssueUrl(event.entityId, jiraBrowseBase)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex h-8 items-center gap-1.5 self-start rounded-md border border-input px-3 text-[12.5px] font-medium hover:bg-accent"
        >
          {t('Open in Jira')}
          <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
        </a>
        <p className="text-[12px] font-semibold uppercase tracking-[.06em] text-muted-foreground">{t('Sector')}</p>
        <div className="grid grid-cols-[1fr_24px_1fr] items-center gap-2">
          <div className={SECTOR_CARD}>
            <p className={CARD_LABEL}>{t('Before')}</p>
            <p className="text-[15px] font-medium text-muted-foreground line-through decoration-zinc-400 dark:decoration-zinc-500">{from}</p>
          </div>
          <ArrowRight className="h-5 w-5 text-muted-foreground" role="img" aria-label={t('to')} />
          <div className={SECTOR_CARD}>
            <p className={CARD_LABEL}>{t('After')}</p>
            <p className="text-[15px] font-semibold">{to}</p>
          </div>
        </div>
        <dl className="text-[13px]">
          {rows.map(([label, value]) => (
            <div key={label} className="grid grid-cols-[140px_1fr] border-b py-3">
              <dt className="text-muted-foreground">{label}</dt>
              <dd className="min-w-0 break-words">{value}</dd>
            </div>
          ))}
        </dl>
      </div>
    </AuditDrawerFrame>
  );
};

export default RealignedTicketDetail;
